import React, { useEffect } from "react";
import { connect } from "react-redux";
import i18n from "i18next";
import SuggestionCard from "../../workflow/SuggestionCard";
import EmptyState from "./SuggestionsTab/EmptyState";
import GenerationProgress from "./SuggestionsTab/GenerationProgress";
import {
  applySuggestionThunk,
  loadSuggestionsThunk,
  setInteractivityStudioBusy,
} from "../../../../common/actions";
import "./SuggestionsTab.scss";

// === Round 27 review #1 + #4 ===
// onPick now dispatches applySuggestionThunk(suggestion) so each card click
// materializes the suggestion as InteractivePlugin items tagged with
// source:"ai" + suggestionId for provenance.
// On mount we dispatch loadSuggestionsThunk(trainingId, sceneId) so the
// Studio shows any server-persisted suggestion list instead of always
// landing on the empty-state CTA (review item #4).
// When a generation job is in flight (busy.jobId set by the regenerate
// thunk) we mount GenerationProgress inline below the cards (review #4).
function SuggestionsTab({
  loading,
  error,
  suggestions,
  scope,
  trainingId,
  sceneId,
  busy,
  onLoadSuggestions,
  onPickSuggestion,
  onClearBusy,
}) {
  useEffect(() => {
    if (trainingId) onLoadSuggestions(trainingId, sceneId);
    // Re-run when the active scene changes so per-scene suggestions stay in
    // sync with the editor's current selection.
  }, [trainingId, sceneId, onLoadSuggestions]);

  const progress =
    busy && busy.jobId ? (
      <GenerationProgress
        jobId={busy.jobId}
        onClose={onClearBusy}
        onSuccess={() => {
          // After a successful generation, refresh the persisted list so the
          // new items appear in the panel.
          if (trainingId) onLoadSuggestions(trainingId, sceneId);
          onClearBusy();
        }}
      />
    ) : null;

  if (loading) {
    return (
      <div className="suggestions-tab__loading" data-testid="suggestions-tab__loading">
        {i18n.t("Workflow.loading", { defaultValue: "Loading…" })}
        {progress}
      </div>
    );
  }
  if (error) {
    return (
      <div className="suggestions-tab__error" data-testid="suggestions-tab__error">
        {error}
        {progress}
      </div>
    );
  }
  if (!suggestions || suggestions.length === 0) {
    return (
      <div data-testid="suggestions-tab__empty">
        <EmptyState trainingId={trainingId} />
        {progress}
      </div>
    );
  }
  return (
    <div className="suggestions-tab__cards" data-testid="suggestions-tab__cards">
      <div className="suggestions-tab__scope-chip">{scope || "—"}</div>
      <div className="suggestions-tab__cards-grid">
        {suggestions.map((s) => (
          <SuggestionCard
            key={s.id}
            suggestion={s}
            locale="en"
            onPick={(picked) => onPickSuggestion(picked)}
          />
        ))}
      </div>
      {progress}
    </div>
  );
}

const mapStateToProps = (state) => {
  const present = (state.undoGroup && state.undoGroup.present) || {};
  return {
    loading: state.workflowSuggestions ? state.workflowSuggestions.loading : false,
    error: state.workflowSuggestions ? state.workflowSuggestions.error : null,
    suggestions: state.workflowSuggestions ? state.workflowSuggestions.suggestions : null,
    scope: state.workflowSuggestions ? state.workflowSuggestions.scope : null,
    sceneId: present.navItemSelected || null,
    busy: state.interactivityStudio ? state.interactivityStudio.busy : null,
  };
};
const mapDispatchToProps = (dispatch) => ({
  onLoadSuggestions: (trainingId, sceneId) => dispatch(loadSuggestionsThunk(trainingId, sceneId)),
  onPickSuggestion: (suggestion) => dispatch(applySuggestionThunk(suggestion)),
  onClearBusy: () => dispatch(setInteractivityStudioBusy(null)),
});
export default connect(mapStateToProps, mapDispatchToProps)(SuggestionsTab);
